import React, { useMemo, useState } from "react";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import SchoolIcon from "@material-ui/icons/School";
import PersonIcon from "@material-ui/icons/Person";
import GroupIcon from "@material-ui/icons/Group";
import InfoIcon from "@material-ui/icons/Info";
import { useDomainFeatures } from "../../context/DomainFeaturesContext";
import "./Faq.css";

const buildFaqData = (brandDomain) => ({
  schools: [
    {
      question: "Q1. Is Montestory designed only for Montessori schools?",
      answer:
        `Yes, ${brandDomain} is exclusively developed based on authentic Montessori principles and classroom structure.`,
    },
    {
      question: "Q2: How long does it take to set up the application for a new school?",
      answer:
        "Typically takes 1–3 working days. Once registered, your admin account is activated after due diligence.",
    },
    {
      question: "Q3: Do we need any special hardware or software to use Montestory?",
      answer:
        "No. Montestory is a web-based application that works on desktops, laptops, tablets, and mobile devices with internet access.",
    },
    {
      question: "Q4: Is training provided for staff?",
      answer:
        "Yes. We offer guided onboarding sessions, video tutorials, and ongoing support to help your team make the most of Montestory.",
    },
  ],
  teachers: [
    {
      question: "Q1: Can I customize lesson plans according to my classroom needs?",
      answer:
        "Montestory provides preloaded Montessori lesson plans. Custom lesson plan editing is limited based on your school's configuration.",
    },
    {
      question: "Q2: How are student activities and progress tracked?",
      answer:
        "Teachers can log daily activities, observations, assignments, and assessments, which are compiled into visual reports and analytics.",
    },
    {
      question: "Q3: Can I collaborate with co-teachers or assistants on the platform?",
      answer:
        "Yes. Role-based access allows collaboration while keeping data secure and organized.",
    },
  ],
  parents: [
    {
      question: "Q1: Can parents see what their child is doing?",
      answer:
        "Yes. The parent app provides updates on activities, attendance, assignments, and upcoming events.",
    },
    {
      question: "Q2: Is my child's data safe?",
      answer:
        "Yes. All data is encrypted and stored securely. We follow strong data protection practices.",
    },
  ],
  general: [
    {
      question: "Q1: What kind of support is available?",
      answer:
        "Our support team is available via email and phone during working hours. We also provide documentation and video guides.",
    },
    {
      question: "Q2: Is there a trial version?",
      answer:
        "Yes. Schools can try Montestory with limited access before deciding to subscribe.",
    },
  ],
});

const TAB_THEMES = {
  schools: {
    accent: "#1769d6",
    soft: "#e8f4fd",
    softHover: "#dbeafe",
    border: "#93c5fd",
  },
  teachers: {
    accent: "#7c4dff",
    soft: "#f3efff",
    softHover: "#ede9fe",
    border: "#c4b5fd",
  },
  parents: {
    accent: "#db2777",
    soft: "#fdf2f8",
    softHover: "#fce7f3",
    border: "#f9a8d4",
  },
  general: {
    accent: "#0d9488",
    soft: "#f0fdfa",
    softHover: "#ccfbf1",
    border: "#5eead4",
  },
};

const TAB_LIST = [
  { label: "For Schools", icon: SchoolIcon, key: "schools" },
  { label: "For Teachers", icon: PersonIcon, key: "teachers" },
  { label: "For Parents", icon: GroupIcon, key: "parents" },
  { label: "General", icon: InfoIcon, key: "general" },
];

export default function Faq() {
  const { brandDomain } = useDomainFeatures();
  const faqData = useMemo(() => buildFaqData(brandDomain), [brandDomain]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [expanded, setExpanded] = useState(0);
  const currentKey = TAB_LIST[selectedTab].key;
  const theme = TAB_THEMES[currentKey];

  const handleTabChange = (index) => {
    setSelectedTab(index);
    setExpanded(0);
  };

  const toggleAccordion = (index) => {
    setExpanded(expanded === index ? -1 : index);
  };

  return (
    <section className="home-faq">
      <div className="home-faq__inner">
        <h2 className="home-faq__title">Frequently Asked Questions</h2>

        <div
          className={`home-faq__card home-faq__card--${currentKey}`}
          style={{
            "--faq-accent": theme.accent,
            "--faq-soft": theme.soft,
            "--faq-soft-hover": theme.softHover,
            "--faq-border": theme.border,
          }}
        >
          <div className="home-faq__tabs" role="tablist" aria-label="FAQ categories">
            {TAB_LIST.map((tab, index) => {
              const Icon = tab.icon;
              const isActive = selectedTab === index;
              const tabTheme = TAB_THEMES[tab.key];
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`home-faq__tab home-faq__tab--${tab.key}${isActive ? " is-active" : ""}`}
                  style={{
                    "--tab-accent": tabTheme.accent,
                    "--tab-soft": tabTheme.soft,
                  }}
                  onClick={() => handleTabChange(index)}
                >
                  <span className="home-faq__tab-icon-wrap" aria-hidden>
                    <Icon className="home-faq__tab-icon" />
                  </span>
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          <div className="home-faq__accordion" role="tabpanel">
            {faqData[currentKey].map((faq, index) => {
              const isOpen = expanded === index;
              return (
                <div
                  key={faq.question}
                  className={`home-faq__item${isOpen ? " is-open" : ""}`}
                >
                  <button
                    type="button"
                    className="home-faq__question"
                    onClick={() => toggleAccordion(index)}
                    aria-expanded={isOpen}
                  >
                    <span className="home-faq__q-num">Q{index + 1}</span>
                    <span className="home-faq__q-text">{faq.question.replace(/^Q\d+[.:]?\s*/i, "")}</span>
                    <ExpandMoreIcon className="home-faq__chevron" />
                  </button>
                  <div className="home-faq__answer" hidden={!isOpen}>
                    <p>{faq.answer}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
